import type { ListEvalSuitesResponse } from "@repo/marketplace-admin"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import { Pin } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { PageHeader } from "@/components/layout"
import { Button } from "@/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot } from "@/components/ui/status"
import { PinSuiteDialog } from "@/features/card-scanner/eval-actions"
import { ScannerGrid } from "@/features/card-scanner/ScannerGrid"
import {
	Instant,
	QueryError,
	useScannerGate,
} from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"

export const Route = createFileRoute("/card-scanner/eval-suites")({
	component: EvalSuitesPage,
})

type SuiteRow = ListEvalSuitesResponse["suites"][number]

function EvalSuitesPage() {
	const allowed = useScannerGate()
	const navigate = useNavigate()
	const { orpc, isAuthReady } = useMarketplaceOrpc()

	const [pinOpen, setPinOpen] = React.useState(false)
	const [makeCurrent, setMakeCurrent] = React.useState<SuiteRow | null>(null)

	const { data, isLoading, isError, error } = useQuery({
		...orpc.cardScanner.listEvalSuites.queryOptions({}),
		enabled: allowed && isAuthReady,
	})

	const columnDefs = React.useMemo<ColDef<SuiteRow>[]>(
		() => [
			{
				headerName: "Version",
				colId: "version",
				sort: "desc",
				minWidth: 120,
				valueGetter: (p) => p.data?.suite.version,
				cellRenderer: (p: ICellRendererParams<SuiteRow>) =>
					p.data ? (
						<span className="inline-flex items-center gap-2">
							<span className="font-medium tabular-nums">
								v{p.data.suite.version}
							</span>
							{p.data.suite.current && <StatusDot tone="ok">current</StatusDot>}
						</span>
					) : null,
			},
			{
				headerName: "Sets",
				colId: "sets",
				minWidth: 90,
				type: "rightAligned",
				cellClass: "tabular-nums",
				valueGetter: (p) => p.data?.sets.length ?? 0,
			},
			{
				headerName: "Verified",
				colId: "verified",
				minWidth: 110,
				sortable: false,
				cellClass: "tabular-nums",
				valueGetter: (p) =>
					p.data
						? `${p.data.sets.filter((set) => set.fingerprint).length}/${p.data.sets.length} verified`
						: "",
			},
			{
				headerName: "Pinned by",
				colId: "pinnedBy",
				minWidth: 140,
				valueGetter: (p) => p.data?.suite.pinnedBy,
			},
			{
				headerName: "Note",
				colId: "note",
				minWidth: 220,
				sortable: false,
				valueGetter: (p) => p.data?.suite.note ?? "",
				cellClass: "text-muted-foreground",
			},
			{
				headerName: "Created",
				colId: "createdAt",
				minWidth: 140,
				valueGetter: (p) => p.data?.suite.createdAt,
				cellRenderer: (p: ICellRendererParams<SuiteRow>) =>
					p.data ? <Instant iso={p.data.suite.createdAt} /> : null,
			},
			{
				headerName: "",
				colId: "actions",
				minWidth: 140,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<SuiteRow>) =>
					p.data && !p.data.suite.current ? (
						<span data-no-row-click>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setMakeCurrent(p.data ?? null)}
							>
								Make current
							</Button>
						</span>
					) : null,
			},
		],
		[],
	)

	if (!allowed) return null

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Eval suites"
				count={data?.suites.length}
				description="Immutable pins of Roboflow versions and built tarballs, the data every eval job scores against"
				actions={
					<Button onClick={() => setPinOpen(true)}>
						<Pin className="h-4 w-4" />
						Pin new suite
					</Button>
				}
			/>

			{isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : isError ? (
				<QueryError what="eval suites" error={error} />
			) : data?.suites.length === 0 ? (
				<p className="text-muted-foreground">
					No eval suite yet. Pin one to give ingested models something to be
					scored on; cut the Roboflow versions first, in the Roboflow UI.
				</p>
			) : (
				<ScannerGrid<SuiteRow>
					columnDefs={columnDefs}
					rowData={data?.suites}
					getRowId={(row) => row.suite.id}
					onRowClicked={(row) =>
						navigate({
							to: "/card-scanner/eval-suites/$suiteId",
							params: { suiteId: row.suite.id },
						})
					}
					gridOptions={{ rowClass: "cursor-pointer" }}
				/>
			)}

			<PinSuiteDialog open={pinOpen} onClose={() => setPinOpen(false)} />

			{makeCurrent && (
				<MakeCurrentDialog
					suite={makeCurrent}
					onClose={() => setMakeCurrent(null)}
				/>
			)}
		</div>
	)
}

function MakeCurrentDialog({
	suite,
	onClose,
}: {
	suite: SuiteRow
	onClose: () => void
}) {
	const { orpc, client } = useMarketplaceOrpc()
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationFn: () =>
			client.cardScanner.setCurrentEvalSuite({ suiteId: suite.suite.id }),
		onSuccess: ({ suite: updated }) => {
			toast.success(`Suite v${updated.version} is now current`)
			queryClient.invalidateQueries({
				queryKey: orpc.cardScanner.listEvalSuites.key(),
			})
			onClose()
		},
		onError: (error) => toast.error(error.message),
	})

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Make suite v{suite.suite.version} current</DialogTitle>
					<DialogDescription>
						New eval jobs will score against these pins from now on. Existing
						results keep their own fingerprints and stay comparable.
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button
						onClick={() => mutation.mutate()}
						disabled={mutation.isPending}
					>
						Confirm
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
